import type { PropsWithChildren } from 'react';

import { isString } from 'lodash-es';

import type { CommonFiltersAppState } from '@iconik-io/shared-lib/src/redux/common_filters/types';

import VARIANTS from '../../constants/variants';
import { toggleLeftNav } from '../../ducks/display/slice';
import { openPanel } from '../../ducks/panels/slice';
import { closePanel } from '../../ducks/panels/thunks';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import SearchInput from '../../ui/search_input/search_input';

import styles from './admin_filters_bar.module.css';

type Props = PropsWithChildren<{
  onSearch?: (value: string) => void;
  showFiltersButton?: boolean;
  customFilters?: CommonFiltersAppState;
  filterPanelPrefix?:
    | 'adminGroupsFilters'
    | 'adminTeamsFilters'
    | 'adminRoleGroupsFilters'
    | 'adminJobsFilters'
    | 'adminRecycleBinAssetsFilters'
    | 'adminRecycleBinCollectionsFilters'
    | 'adminRecycleBinFileSetsFilters'
    | 'adminRecycleBinFormatsFilters'
    | 'adminStoragesFilters'
    | 'adminTranscodersFilters'
    | 'adminFaceRecognitionFilters'
    | 'adminSharesFilters'
    | 'adminUsersFilters'
    | 'adminSystemDomainsFilters'
    | 'currentUserSharesFilters';
  shouldAutoFocus?: boolean;
  filtersButtonDisabled?: boolean;
}>;

export const getBadgeCount = (filters?: CommonFiltersAppState) => {
  if (!filters) {
    return null;
  }
  const filtersLength = Object.values(filters.filterValues).filter((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (isString(value)) {
      return value.length > 0;
    }
    return true;
  }).length;
  const facetsLength = Object.keys(filters.facetValues).filter(
    (item) => filters.facetValues[item].length > 0,
  ).length;

  return filtersLength + facetsLength;
};

const AdminFiltersBar = ({
  filterPanelPrefix,
  onSearch,
  showFiltersButton = true,
  customFilters,
  shouldAutoFocus = true,
  filtersButtonDisabled = false,
  children,
}: Props) => {
  const breakpoint = useAppSelector((state) => state.displayReducer.breakpoint);
  const isMobile = breakpoint < 3;
  const dispatch = useAppDispatch();
  const panelsState = useAppSelector((state) => state.panels.openedPanels);
  const leftNav = useAppSelector((state) => state.displayReducer.leftNav);

  const filterPanel = useAppSelector((state) =>
    filterPanelPrefix ? state.panelsState[filterPanelPrefix] : undefined,
  );

  const badgeCount = showFiltersButton
    ? getBadgeCount(customFilters ?? filterPanel)
    : 0;

  return (
    <div className={styles.filter_bar_container}>
      {onSearch ? (
        <SearchInput
          buttonClassName={styles.input_button}
          inputClassName={styles.input}
          className={styles.input_container}
          placeholder="Search..."
          onSearch={onSearch}
          name="filterSearch"
          autoFocus={shouldAutoFocus}
          canClear
          value={filterPanel?.query}
        />
      ) : null}
      {children}
      {showFiltersButton ? (
        <Button
          iconName="filter"
          noBorder
          variant={VARIANTS.WHITE}
          disabled={filtersButtonDisabled}
          onClick={() => {
            if (isMobile) {
              dispatch(toggleLeftNav(!leftNav));
              return;
            }

            if (panelsState.filters) {
              dispatch(closePanel('filters'));
              return;
            }
            dispatch(openPanel('filters'));
          }}
        >
          {children ? 'MORE FILTERS' : 'FILTERS'}{' '}
          {badgeCount ? ` (${badgeCount})` : ''}
        </Button>
      ) : null}
    </div>
  );
};

export default AdminFiltersBar;
